import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountingService } from '../../../services/accounting.service';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';

import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-accounting-reports',
  standalone: true,
  imports: [CommonModule, CardModule, TableModule, ButtonModule, SelectButtonModule, FormsModule, DatePickerModule],
  template: `
    <div class="p-4">
      <div class="flex justify-content-between align-items-center mb-4">
        <h1 class="text-3xl font-bold m-0">Reports</h1>
        <p-button label="Export to PDF" icon="pi pi-file-pdf" severity="secondary"></p-button>
      </div>

      <div class="surface-card p-4 border-round shadow-1 mb-4">
        <div class="flex flex-wrap gap-4 align-items-end">
            <div class="flex flex-column gap-2">
                <label class="font-bold">Start Date</label>
                <p-datepicker [(ngModel)]="startDate" [showIcon]="true" dateFormat="yy-mm-dd"></p-datepicker>
            </div>
            <div class="flex flex-column gap-2">
                <label class="font-bold">End Date</label>
                <p-datepicker [(ngModel)]="endDate" [showIcon]="true" dateFormat="yy-mm-dd"></p-datepicker>
            </div>
            <p-button label="Update Reports" icon="pi pi-refresh" (onClick)="loadData()"></p-button>
            <div class="flex gap-2">
                <p-button label="This Year" [text]="true" (onClick)="setPreset('year')"></p-button>
                <p-button label="This Month" [text]="true" (onClick)="setPreset('month')"></p-button>
            </div>
        </div>
      </div>

      <div class="flex justify-content-center mb-4">
        <p-selectButton [options]="reportOptions" [(ngModel)]="selectedReport" optionLabel="label" optionValue="value" (onChange)="loadData()"></p-selectButton>
      </div>

      <div *ngIf="selectedReport === 'pl'">
        <div *ngIf="profitLoss" class="max-w-30rem mx-auto mt-4 p-4 surface-card border-round shadow-2 border-top-3 border-primary">
            <h2 class="text-center mb-2">Profit & Loss Statement</h2>
            <p class="text-center text-secondary mb-5">
                {{ startDate | date:'mediumDate' }} - {{ endDate | date:'mediumDate' }}
            </p>
            
            <h3 class="border-bottom-1 border-300 pb-2 mb-3 text-secondary font-medium">INCOME</h3>
            <div *ngFor="let item of profitLoss.income" class="flex justify-content-between mb-2 px-2">
              <span>{{ item.category }}</span>
              <span class="font-bold text-green-600">{{ item.amount | currency }}</span>
            </div>
            
            <h3 class="border-bottom-1 border-300 pb-2 mt-4 mb-3 text-secondary font-medium">EXPENSES</h3>
            <div *ngFor="let item of profitLoss.expenses" class="flex justify-content-between mb-2 px-2">
              <span>{{ item.category }}</span>
              <span class="text-red-500">({{ item.amount | currency }})</span>
            </div>

            <div class="mt-5 pt-4 border-top-2 border-primary flex justify-content-between align-items-center">
                <span class="text-xl font-bold">NET PROFIT</span>
                <span class="text-2xl font-bold" [class.text-green-500]="profitLoss.net_profit >= 0" [class.text-red-500]="profitLoss.net_profit < 0">
                    {{ profitLoss.net_profit | currency }}
                </span>
            </div>
        </div>
      </div>
      
      <div *ngIf="selectedReport === 'cash'">
        <div class="max-w-30rem mx-auto mt-4">
           <p-card [header]="'Account Balances Summary - As of ' + (endDate | date:'mediumDate')">
            <p-table [value]="cashSummary" styleClass="p-datatable-sm p-datatable-gridlines">
              <ng-template pTemplate="header">
                <tr>
                  <th>Account</th>
                  <th class="text-right">Balance</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td>{{ item.account }}</td>
                  <td class="text-right font-bold" [class.text-red-500]="item.balance < 0">
                    {{ item.balance | currency }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
           </p-card>
        </div>
      </div>
    </div>
  `
})
export class ReportsComponent implements OnInit {
  profitLoss: any;
  cashSummary: any[] = [];
  selectedReport = 'pl';

  startDate: Date = new Date(new Date().getFullYear(), 0, 1);
  endDate: Date = new Date();

  reportOptions = [
    { label: 'Profit & Loss', value: 'pl' },
    { label: 'Cash Summary', value: 'cash' }
  ];

  constructor(private accountingService: AccountingService) { }

  ngOnInit() {
    this.loadData();
  }

  setPreset(type: 'year' | 'month') {
    const now = new Date();
    if (type === 'year') {
      this.startDate = new Date(now.getFullYear(), 0, 1);
      this.endDate = now;
    } else {
      this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      this.endDate = now;
    }
    this.loadData();
  }

  loadData() {
    const start = this.startDate.toISOString().split('T')[0];
    const end = this.endDate.toISOString().split('T')[0];

    if (this.selectedReport === 'pl') {
      this.accountingService.getProfitLoss(start, end).subscribe(data => this.profitLoss = data);
    } else {
      this.accountingService.getCashSummary(end).subscribe(data => this.cashSummary = data);
    }
  }
}
